import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useProjects } from "@/hooks/useData";
import { api, imgUrl, formatApiErrorDetail } from "@/lib/api";
import { AdminHeader, DeleteButton } from "@/components/admin/AdminUI";
import { ImageUpload } from "@/components/admin/ImageUpload";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Pencil } from "lucide-react";

const EMPTY = { title: "", country: "", project_type: "", category: "Whole-Home", year: "", client_type: "", description: "", cover_image: "", gallery: [], scope: [], featured: false, order: 0 };
const toLines = (arr) => (arr || []).join("\n");
const fromLines = (s) => s.split("\n").map((x) => x.trim()).filter(Boolean);
const toCsv = (arr) => (arr || []).join(", ");
const fromCsv = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);

export default function ManageProjects() {
  const { data: items = [] } = useProjects();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [galleryText, setGalleryText] = useState("");
  const [scopeText, setScopeText] = useState("");
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openNew = () => { setEditing(null); setForm(EMPTY); setGalleryText(""); setScopeText(""); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ ...EMPTY, ...it }); setGalleryText(toLines(it.gallery)); setScopeText(toCsv(it.scope)); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, gallery: fromLines(galleryText), scope: fromCsv(scopeText), order: Number(form.order) };
      if (editing) await api.put(`/admin/projects/${editing.id}`, payload);
      else await api.post("/admin/projects", payload);
      qc.invalidateQueries({ queryKey: ["projects"] });
      toast.success(editing ? "Project updated" : "Project added");
      setOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    await api.delete(`/admin/projects/${id}`);
    qc.invalidateQueries({ queryKey: ["projects"] });
    toast.success("Deleted");
  };

  return (
    <div data-testid="manage-projects">
      <AdminHeader eyebrow="Completed Work" title="Projects" onAdd={openNew} addLabel="Add Project" />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {items.map((p) => (
          <div key={p.id} className="bg-white border border-[#ECEAE4]" data-testid={`admin-project-${p.id}`}>
            <div className="aspect-[16/10] bg-[#F6F5F2] overflow-hidden">
              <img src={imgUrl(p.cover_image)} alt={p.title} className="w-full h-full object-cover" />
            </div>
            <div className="p-4">
              <p className="label-eyebrow text-[9px] text-[#9E8773]">{p.country} · {p.project_type}</p>
              <h3 className="font-display text-xl font-medium text-[#2C2A29] mt-1">{p.title}</h3>
              <div className="flex justify-between items-center mt-2">
                {p.featured ? <span className="label-eyebrow text-[9px] text-[#9E8773]">Featured</span> : <span />}
                <div className="flex">
                  <button onClick={() => openEdit(p)} className="text-[#5E5A54] hover:text-[#2C2A29] p-2" data-testid={`edit-project-${p.id}`}><Pencil size={15} /></button>
                  <DeleteButton onConfirm={() => remove(p.id)} label="project" testid={`delete-project-${p.id}`} />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#FFFFFF] rounded-none border-[#ECEAE4] max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-3xl">{editing ? "Edit" : "New"} Project</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <Field label="Title" required value={form.title} onChange={(v) => set("title", v)} testid="pr-title" />
            <div className="grid grid-cols-2 gap-4">
              <Field label="Country" value={form.country} onChange={(v) => set("country", v)} testid="pr-country" />
              <Field label="Project type" value={form.project_type} onChange={(v) => set("project_type", v)} testid="pr-type" />
              <Field label="Category" value={form.category} onChange={(v) => set("category", v)} testid="pr-category" />
              <Field label="Year" value={form.year} onChange={(v) => set("year", v)} testid="pr-year" />
              <Field label="Client type" value={form.client_type} onChange={(v) => set("client_type", v)} testid="pr-client" />
              <Field label="Display order" type="number" value={form.order} onChange={(v) => set("order", v)} testid="pr-order" />
            </div>
            <ImageUpload value={form.cover_image} onChange={(v) => set("cover_image", v)} label="Cover image" />
            <div className="space-y-1.5">
              <Label className="label-eyebrow text-[#5E5A54]">Description</Label>
              <Textarea rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} className="rounded-none bg-white border-[#ECEAE4]" data-testid="pr-description" />
            </div>
            <div className="space-y-1.5">
              <Label className="label-eyebrow text-[#5E5A54]">Scope (comma separated)</Label>
              <Input value={scopeText} onChange={(e) => setScopeText(e.target.value)} placeholder="Furniture, Cabinetry, Lighting" className="rounded-none bg-white border-[#ECEAE4]" data-testid="pr-scope" />
            </div>
            <div className="space-y-1.5">
              <Label className="label-eyebrow text-[#5E5A54]">Gallery image URLs (one per line)</Label>
              <Textarea rows={3} value={galleryText} onChange={(e) => setGalleryText(e.target.value)} className="rounded-none bg-white border-[#ECEAE4]" data-testid="pr-gallery" />
            </div>
            <div className="flex items-center justify-between border border-[#ECEAE4] bg-white px-4 py-3">
              <Label className="label-eyebrow text-[#5E5A54]">Feature on homepage</Label>
              <Switch checked={form.featured} onCheckedChange={(v) => set("featured", v)} data-testid="pr-featured" />
            </div>
            <Button type="submit" disabled={saving} className="w-full rounded-none bg-[#2C2A29] text-[#FFFFFF] h-12 label-eyebrow text-[10px]" data-testid="pr-save">
              {saving ? "Saving…" : "Save Project"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Field = ({ label, value, onChange, type = "text", required, testid }) => (
  <div className="space-y-1.5">
    <Label className="label-eyebrow text-[#5E5A54]">{label}</Label>
    <Input type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)} className="rounded-none bg-white border-[#ECEAE4]" data-testid={testid} />
  </div>
);
